import React, { useState } from 'react';
import CustomerInfo from './CustomerInfo';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import { generateCustomerId } from '../../services/contextService';

function CustomerInfoSection({ context, setContext, onSave, saving }) {
  const [status, setStatus] = useState(null);

  const sanitizeContext = (ctx) => {
    // Ensure numeric fields are actually numbers, not strings
    const numericFields = [
      'cost_per_agent',
      'professional_services_price',
      'bom_price',
      'bom_length_months',
      'monthly_usage_costs'
    ];

    const sanitized = { ...ctx };

    numericFields.forEach(field => {
      if (sanitized[field] !== undefined && sanitized[field] !== null) {
        const num = parseFloat(sanitized[field]);
        sanitized[field] = isNaN(num) ? 0 : num;
      }
    });

    return sanitized;
  };

  const handleLoadContext = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedContext = JSON.parse(e.target.result);
        // Sanitize to ensure numeric fields are numbers
        const sanitizedContext = sanitizeContext(loadedContext);
        setContext(sanitizedContext);
        setStatus({ type: 'success', message: 'Context loaded successfully!' });
      } catch (error) {
        setStatus({ type: 'error', message: 'Failed to parse context file' });
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadContext = () => {
    if (!context.customer) {
      setStatus({ type: 'error', message: 'Please enter customer name first' });
      return;
    }

    const customerId = generateCustomerId(context.customer);
    const jsonString = JSON.stringify(context, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${customerId}-context.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setStatus({ type: 'success', message: 'Context downloaded successfully!' });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Customer Context</h2>
      <p className="text-gray-600 text-sm mb-6">Enter basic customer information or load an existing context file to get started.</p>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      {/* Load/Download Context */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Load Context
          </label>
          <input
            type="file"
            accept=".json"
            onChange={handleLoadContext}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Download Context
          </label>
          <Button
            variant="secondary"
            onClick={handleDownloadContext}
            disabled={!context.customer}
            className="w-full"
          >
            Download JSON
          </Button>
        </div>
      </div>

      <CustomerInfo context={context} onChange={setContext} />

      <Button
        variant="primary"
        onClick={onSave}
        disabled={saving}
        className="w-full mt-4"
      >
        {saving ? 'Saving...' : 'Save Context'}
      </Button>

      {!context.customer && (
        <p className="text-sm text-gray-500 mt-2">
          Customer name is required to save context
        </p>
      )}
    </div>
  );
}

export default CustomerInfoSection;
